import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { API_URL, MENTION_REGEX, resolveEntityRoute, resolveTypeFromLabel } from '../../lib/mentions';

const normalize = (value) =>
  (value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');

export function MentionText({ text, campaignId, language = 'fr', className }) {
  const navigate = useNavigate();

  if (!text) {
    return <span className={cn('whitespace-pre-wrap', className)} />;
  }

  const parts = [];
  let cursor = 0;
  let index = 0;
  for (const match of text.matchAll(MENTION_REGEX)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (start > cursor) {
      parts.push({ type: 'text', value: text.slice(cursor, start), key: `text-${index}` });
      index += 1;
    }
    parts.push({
      type: 'mention',
      value: match[0],
      label: (match[1] || '').trim(),
      typeLabel: (match[2] || '').trim(),
      key: `mention-${index}`
    });
    index += 1;
    cursor = end;
  }
  if (cursor < text.length) {
    parts.push({ type: 'text', value: text.slice(cursor), key: `text-${index}` });
  }

  const openMention = async (label, typeLabel) => {
    if (!label || !campaignId) return;
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/mentions/search`, {
        params: { q: label, per_group: 50 }
      });
      const requestedType = resolveTypeFromLabel(typeLabel);
      const candidates = (response.data?.groups || []).flatMap((group) => group.items || []);
      const exact = candidates.filter((item) => normalize(item.name) === normalize(label));
      const byType = requestedType ? exact.filter((item) => item.type === requestedType) : exact;
      const target = byType[0] || exact[0] || null;
      if (!target) {
        toast.info(language === 'fr' ? 'Mention non resolue' : 'Mention not resolved');
        return;
      }
      const route = resolveEntityRoute(campaignId, target.type, target.id);
      if (!route) {
        toast.info(language === 'fr' ? 'Aucune page detail pour cette mention' : 'No detail page for this mention');
        return;
      }
      navigate(route);
    } catch (error) {
      toast.error(language === 'fr' ? 'Impossible d ouvrir la mention' : 'Unable to open mention');
    }
  };

  return (
    <span className={cn('whitespace-pre-wrap', className)}>
      {parts.map((part) =>
        part.type === 'text' ? (
          <React.Fragment key={part.key}>{part.value}</React.Fragment>
        ) : (
          <button
            key={part.key}
            type="button"
            className="cursor-pointer rounded px-0.5 font-medium text-primary hover:underline"
            onClick={() => openMention(part.label, part.typeLabel)}
          >
            @{part.label}
          </button>
        )
      )}
    </span>
  );
}
